import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

type StatVariant = 'success' | 'error' | 'warning' | 'default';

interface StatCardProps {
  variant?: StatVariant;
}

export const StatCard = styled('div', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'variant'
})<StatCardProps>`
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(5)};

  h3 {
    font-size: ${({ theme }) => theme.font.size.xs};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-value {
    font-size: ${({ theme }) => theme.font.size.xxl};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    margin: 0 0 ${({ theme }) => theme.spacing(1)} 0;
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;
    line-height: 1.15;

    color: ${({ variant, theme }) => {
      switch (variant) {
        case 'success': return theme.color.green;
        case 'error':   return theme.color.red;
        case 'warning': return theme.color.yellow;
        default:        return theme.font.color.primary;
      }
    }};
  }

  small {
    font-size: ${({ theme }) => theme.font.size.sm};
    color: ${({ theme }) => theme.font.color.tertiary};
  }
`;

// Default card. Flat — hairline border on canvas-soft page bg does the
// visual separation. NO shadow. See ui/docs/simkura-design.md / Components / Card.
export const DetailCard = styled.section`
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(5)};

  h2 {
    font-size: ${({ theme }) => theme.font.size.lg};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    line-height: 1.2;
    letter-spacing: -0.1px;
    margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  }

  dl {
    margin: 0;
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: ${({ theme }) => theme.spacing(2, 4)};
    align-items: baseline;
  }

  dt {
    font-size: ${({ theme }) => theme.font.size.md};
    color: ${({ theme }) => theme.font.color.tertiary};
    font-weight: ${({ theme }) => theme.font.weight.regular};
  }

  dd {
    margin: 0;
    font-size: ${({ theme }) => theme.font.size.md};
    color: ${({ theme }) => theme.font.color.primary};
  }
`;

// Floating variant for modals, dropdowns, popovers. Adds level-2 shadow
// and drops the hairline (the shadow does the lifting).
export const FloatingCard = styled.section`
  background: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  box-shadow: ${({ theme }) => theme.boxShadow.level2};
  padding: ${({ theme }) => theme.spacing(5)};
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;
